import React, { useEffect, useState } from "react";
import { commonsHelper } from "../../../helpers";
import { debounce } from "lodash-es";
import { SelectionProcessesService } from "../../../src/core/services/selection_processes_service";
import { metadata } from "../../selection_processes/settings/initialStates";
import { ListService } from "../../../src/core/services/list_service";
import FormUserColumns from "../form_user_columns/FormUserColumns";
import { TemplatesService } from "@frontend/src/core/services/templates_service";
import { openNotificationWithIcon } from "@frontend/src/utilities/notificationWithIcon";
import { GeneralModal } from '@rankmi/ats-app'

const noData = {
  active: true,
  id: null,
  title: "Sin plantilla"
}

const ModalUserColumns = ({
  isModalVisible,
  onCancelModal,
  columnId,
  currentUser,
  setShowUserColumn,
  selectionProcessId
}) => {
  const [currentColumn, setCurrentColumn] = useState(null);
  const [userList, setUserList] = useState({
    data: [],
    pagination: metadata,
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false); 
  const [searchTerm, setSearchTerm] = React.useState("");
  const [templates, setTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([])
  const listService = new ListService();
  const selectionProcessesService = new SelectionProcessesService();
  const templatesService = new TemplatesService();

  useEffect(() => {
    if (!isModalVisible) {
      setCurrentColumn(null);
      setDisableSubmit(false);
      setSelectedUsers([]);
      setTemplates([]);
      setEmailTemplates([]);
      setSearchTerm("");
      return;
    }

    handleGetList();
  }, [isModalVisible, columnId]);

  useEffect(() => {
    if (currentColumn) handleGetAllUsers(userList.pagination.currentPage);
  }, [currentColumn]);

  useEffect(() => {
    if (currentColumn && templates.length == 0) fetchTemplates();
  }, [currentColumn, templates]);

  const userColumns = currentColumn?.user_columns ?? [];
  const reportTemplates = currentColumn?.report_templates ?? [];
  const externalUsers = currentColumn?.external_users ?? [];

  const initValues = currentColumn
    ? {
        name: currentColumn.name,
        order: currentColumn.order,
        user_columns_active: currentColumn.user_columns_active,
        enable_limit_applicants: currentColumn.enable_limit_applicants,
        author_active: currentColumn.author_active,
        responsible_active: currentColumn.responsible_active,
        managers_active: currentColumn.managers_active,
        slaSetting: currentColumn.sla_setting,
        user_columns: userColumns.map((item) => item.user_id),
        applicant_requirements: currentColumn.applicant_requirements,
        move_templates: currentColumn.move_templates?.id,
        disqualify_templates: currentColumn.disqualify_templates?.id,
        report_templates:
          reportTemplates.length > 0 ? reportTemplates[0].id : null,
        external_users:
          externalUsers.length > 0
            ? externalUsers.reduce(
                (prev, current, index) =>
                  index == 0
                    ? (prev += `${current.email}`)
                    : (prev += `;${current.email}`),
                ""
              )
            : "",
      }
    : null;

  const handleGetList = () => {
    listService.getById(
      columnId,
      (data) => {
        setCurrentColumn(data);
      },
      (err) => console.error(err)
    );
  };

  const handleTemplateSettings = (data) => {
    const user_profile = data?.user_profile ? [noData, ...data.user_profile] : [noData]
    const email = data?.emails ? [noData, ...data.emails]: [noData]
    setTemplates(user_profile)
    setEmailTemplates(email)
  }

  const handleGetAllUsers = (page, term) => {
    setLoading(true);
    selectionProcessesService
      .listUsers(
        userColumns.map((item) => item.user_id),
        page,
        10,
        term,
        ""
      )
      .then(({ data, headers }) => {
        const mainData = {
          data,
          pagination: commonsHelper.buildMetadata(
            headers["current-page"],
            headers["total-pages"],
            headers["total-count"]
          ),
        };

        if (page > 1) {
          setUserList((value) => ({
            data: value.data.concat(mainData.data),
            pagination: mainData.pagination,
          }));
        } else {
          setUserList(mainData);
        }

        if (!term) {
          setUserList((value) => ({
            data: value.data.concat(
              mainData.data.filter((it) => selectedUsers.includes(it.id))
            ),
            pagination: mainData.pagination,
          }));
        }
        setLoading(false);
      });
  };

  const handleInfiniteScroll = ({
    target: { scrollHeight, scrollTop, clientHeight },
  }) => {
    const { nextPage } = userList.pagination;
    if (scrollHeight < scrollTop + clientHeight + 50 && nextPage && !loading) {
      searchTerm
        ? handleGetAllUsers(nextPage, searchTerm)
        : handleGetAllUsers(nextPage);
    }
  };

  const debouncedSearch = React.useCallback(
    debounce(handleGetAllUsers, 300),
    []
  );

  const onSearch = (term) => {
    setSearchTerm(term);
    term ? debouncedSearch(1, term) : setUserList(userList);
  };

  const handleSelect = (value) => {
    const selectedUserData = userList.data.find((item) => item.id === value);
    setSelectedUsers([...selectedUsers, selectedUserData]);
  };

  const handleDeselect = (value) => {
    const selectedUsersData = selectedUsers.filter((item) => item.id !== value);
    setSelectedUsers(selectedUsersData);
  };

  const onEditSubmit = (e, form) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (err) {
        return;
      }

      setDisableSubmit(true);
      values.report_templates = [values.report_templates];
      values.external_users =
        values.external_users?.length > 0
          ? values.external_users.split(";")
          : [];
      values["sla_setting_attributes"] = {
        id: currentColumn?.sla_setting?.id,
        active: values.sla_count ? true : false,
        max_days: values.sla_count ? values.sla_count : 0,
      };
      if (values.applicant_requirements) {
        values.applicant_requirements = values.applicant_requirements.map(
          (id) => ({
            value: values.applicant_requirement_value[id],
            condition: values.applicant_requirement_condition[id],
            required_type: "relation_table",
            required_field: "applicant_files",
          })
        );
      }
      listService.update(
        currentColumn.id,
        values,
        () => {
          setDisableSubmit(false);
          openNotificationWithIcon("success", "Guardado correctamente", "");
          setShowUserColumn(false);
          setSelectedUsers([]);
          setTemplates([]);
          window.location.href = `${window.location.origin}/selection_processes/${selectionProcessId}`;
        },
        (err) => {
          setDisableSubmit(false);
          console.error(err);
        }
      );
    });
  };

  const fetchTemplates = () => {
    templatesService.getAll(
      currentUser.organization_id,
      handleTemplateSettings,
      () => {}
    );
  };

  return (
    <GeneralModal
      className="ModalEditStep"
      title={"Configuraciones de etapa"}
      visible={isModalVisible}
      onClose={onCancelModal}
      destroyOnClose
    >
      {initValues ? (
        <FormUserColumns
          initValues={initValues}
          onSearch={onSearch}
          handleSelect={handleSelect}
          handleInfiniteScroll={handleInfiniteScroll}
          loading={loading}
          userList={userList}
          disableSubmit={disableSubmit}
          handleSubmitEdit={onEditSubmit}
          handleDeselect={handleDeselect}
          templates={templates}
          emailTemplates={emailTemplates}
          onCancel={onCancelModal}
        />
      ) : null}
    </GeneralModal>
  );
};

export default ModalUserColumns;
